/* global JSX */

import "./styles.css";
import "leaflet/dist/leaflet.css";
import * as L from "leaflet";
import * as ReactDOM from "react-dom";
import * as React from "react";
import union from "@turf/union";
import bbox from "@turf/bbox";
import bboxPolygon from "@turf/bbox-polygon";
import { Feature, MultiPolygon, points, Polygon, polygon } from "@turf/helpers";
import { createNotEmittedStatement } from "typescript";

var app = document.getElementById("app");
var tile = 128;
var mapWidth = 6000;
var projectionFactor = tile / mapWidth;
var Simple = L.Util.extend({}, L.CRS.Simple, {
  transformation: new L.Transformation(projectionFactor, 0, -projectionFactor, 0)
});

var map = L.map(app, {
  crs: Simple,
  center: L.latLng(0, 0),
  zoom: 1,
  minZoom: 1,
  maxZoom: 12,
  preferCanvas: true
});

L.tileLayer("https://convoytrucking.net/static/map/{z}_{x}_{y}.png", {
  minNativeZoom: 1,
  maxNativeZoom: 3,
  tileSize: 128,
  bounds: [
    [-3000, -3000],
    [3000, 3000]
  ]
}).addTo(map);

function ctUrl(path: string) {
  if (path.toLowerCase().startsWith("http")) {
    return path;
  }
  return `https://convoytrucking.net${path}`;
}

function colorIntToHex(n: number) {
  var r = (n & 0xff000000) >> 24;
  if (r < 0) r += 256;
  var g = (n & 0x00ff0000) >> 16;
  var b = (n & 0x0000ff00) >> 8;

  var rs = r.toString(16);
  var gs = g.toString(16);
  var bs = b.toString(16);

  while (rs.length < 2) rs = "0" + r;
  while (gs.length < 2) gs = "0" + g;
  while (bs.length < 2) bs = "0" + b;

  return "#" + rs + gs + bs;
}

(async () => {
  var basemap = L.featureGroup().addTo(map);
  for await (let data of apiGetAll(ctUrl("/api/minimap?page_size=10000")) {
    for (var r of data.results) {
      basemap.addLayer(
        L.rectangle(
          [
            [r["y1"], r["x1"]],
            [r["y2"], r["x2"]]
          ],
          {
            fill: true,
            fillOpacity: 1,
            fillColor: colorIntToHex(r.color),
            stroke: true,
            weight: 1,
            color: colorIntToHex(r.color),
  
            //@ts-expect-error
            data: r
          }
        )
      )
    }
  }
})();

function entityLink(
  type,
  entity,
  id = "id",
  name = "name",
  useName = false,
  customName = null
) {
  return (
    <a href={ctUrl(`/${type}/${entity[useName ? name : id]}`)} target="_blank">
      {customName || entity[name]}
    </a>
  );
}

L.control.scale().addTo(map);

var hires = L.imageOverlay(
  "https://images2.imgbox.com/c9/4f/3cID8bkw_o.jpg",
  [
    [-3000, -3000],
    [3000, 3000]
  ],
  { opacity: 0 }
).addTo(map);

map.on("zoom", (ev) => {
  var zoom = map.getZoom();
  var o = Math.min(Math.max(1 - (5 - zoom) / 2, 0), 1);
  hires.setOpacity(o);
});

var numberFormatter = new Intl.NumberFormat();
var relativeTimeFormatter = new Intl.RelativeTimeFormat();

var housePopup = (r) => (
  <>
    <div>
      <strong>House</strong>
    </div>
    <div>
      <strong>
        {entityLink(
          "house",
          r,
          undefined,
          undefined,
          undefined,
          `${r.id} ${r.location}`
        )}
      </strong>
    </div>
    <img
      src={ctUrl(`/media/houses/small/${r.image_name}`)}
      alt={`House ${r.id} ${r.location}`}
      style={{ width: "144px" }}
    />
    <div>
      <strong>
        {r.ownerid ? (
          <>Owned by {entityLink("player", r.ownerid)}</>
        ) : (
          <>For sale</>
        )}
      </strong>
    </div>
    <div>{numberFormatter.format(r.parking)} slots</div>
    <div>${numberFormatter.format(r.price)}</div>
    <div>Parking: {numberFormatter.format(r.range)}m</div>
    <div>Interior: {r.interior}</div>
  </>
);

var clubBasePopup = (r) => {
  var paidDiff = r.paid_until - +new Date() / 1000;
  var diffUnit =
    paidDiff < 60
      ? "second"
      : paidDiff < 60 * 60
      ? "minute"
      : paidDiff < 24 * 60 * 60
      ? "hour"
      : "day";
  var unitDivider =
    diffUnit === "day"
      ? 24 * 60 * 60
      : diffUnit === "hour"
      ? 60 * 60
      : diffUnit === "minute"
      ? 60
      : 1;

  return (
    <>
      <div>
        <strong>Club base</strong>
      </div>
      <div>
        <strong>
          {r.club ? <>Owned by {entityLink("club", r.club)}</> : <>For sale</>}
        </strong>
      </div>
      {r.paid_until ? (
        <div>
          Lease expiry:
          {relativeTimeFormatter.format(
            Math.floor(paidDiff / unitDivider),
            diffUnit
          )}
        </div>
      ) : null}
      <div>Upfront cost: ${numberFormatter.format(r.base_price)}</div>
      <div>Renewal: ${numberFormatter.format(r.base_price / 10)} per month</div>
    </>
  );
};

var popupContainer = document.createElement("div");
var layers = [
  {
    name: "Vehicle dealers",
    apiUrl: "/api/dealers",
    iconUrl: "/static/map/icons/dealer.gif",
    x: "dealerx",
    y: "dealery",
    popup: (r) => (
      <>
        <div>
          <strong>Vehicle dealer</strong>
        </div>
        <div>
          <strong>
            {entityLink("dealers", r, "dealerid", "dealername", true)}
          </strong>
        </div>
      </>
    ),
    visible: true
  },
  {
    name: "Shops",
    apiUrl: "/api/shops",
    iconUrl: "/static/map/icons/shop.gif",
    popup: (r) => (
      <>
        <div>
          <strong>Shop</strong>
        </div>
        <div>
          <strong>{r.location}</strong>
        </div>
      </>
    ),
    visible: true
  },
  {
    name: "Gas stations",
    apiUrl: "/api/gas_stations",
    iconUrl: "/static/map/icons/gas_station.gif",
    popup: (r) => (
      <>
        <div>
          <strong>Gas Station</strong>
        </div>
        <div>
          <strong>{r.location}</strong>
        </div>
      </>
    ),
    visible: true
  },
  {
    name: "Parking",
    apiUrl: "/api/carparks",
    iconUrl: "/static/map/icons/carpark.gif",
    popup: (r) => (
      <>
        <div>
          <strong>
            {r.houseid === -2
              ? "Car park"
              : r.houseid === -1
              ? "Truck park"
              : "Parking"}
          </strong>
        </div>
        <div>
          <strong>{r.location}</strong>
        </div>
        <div>Parking: {numberFormatter.format(r.r)}m</div>
      </>
    ),
    visible: true
  },
  {
    name: "Club bases (for sale)",
    apiUrl: "/api/club_bases?club__isnull=true",
    x: "pickup_x",
    y: "pickup_y",
    iconUrl: "/static/map/icons/base.gif",
    popup: clubBasePopup,
    visible: true
  },
  {
    name: "Club bases (owned)",
    apiUrl: "/api/club_bases?club__isnull=false",
    x: "pickup_x",
    y: "pickup_y",
    iconUrl: "https://sampwiki.blast.hk/wroot/images2/6/6c/Icon_12.gif",
    popup: clubBasePopup
  },
  {
    name: "Houses (for sale)",
    apiUrl: "/api/houses?ownerid__isnull=true",
    iconUrl: "/static/map/icons/house_sale.gif",
    popup: housePopup
  },
  {
    name: "Houses (owned)",
    apiUrl: "/api/houses?ownerid__isnull=false",
    iconUrl: "/static/map/icons/house_bought.gif",
    popup: housePopup
  },
  {
    name: "Server vehicles",
    apiUrl: "/api/server_vehicles",
    iconUrl: "https://sampwiki.blast.hk/wroot/images2/8/8c/Icon_60.gif",
    popup: (r) => (
      <>
        <div>
          <strong>Server vehicle</strong>
        </div>
        <div>
          <strong>
            {entityLink("vehicles", r.model, "modelid", undefined, false)}
          </strong>
        </div>
      </>
    )
  },
  {
    name: "Private vehicles",
    apiUrl: "/api/private_vehicles/",
    iconUrl: "https://sampwiki.blast.hk/wroot/images2/7/7c/Icon_61.gif",
    popup: (r) => (
      <>
        <div>
          <strong>Private vehicle</strong>
        </div>
        <div>
          <strong>
            {entityLink("vehicles", r.model, "modelid", undefined, false)}
          </strong>
        </div>
        <div>
          <strong>
            <code>{r.reg}</code>
          </strong>
        </div>
        <div>
          <strong>Owned by {entityLink("player", r.ownerid)}</strong>
        </div>
        {r.clamped ? (
          <div>
            <strong>Clamped</strong>
          </div>
        ) : null}
        {r.forsale ? (
          <div>
            <strong>For sale for ${numberFormatter.format(r.forsale)}</strong>
          </div>
        ) : null}
        {r.clubid ? (
          <div>
            <strong>Shared with {entityLink("club", r.clubid)}</strong>
          </div>
        ) : null}
        <div>Odo: {numberFormatter.format(r.odo / 1000)}km</div>
        <div>Health: {(100 * r.hp) / 1000}%</div>
        <div>Last update: {r.lastupdate}</div>
      </>
    )
  },
  {
    name: "Industrial sites",
    apiUrl: "/api/industrial_sites",
    iconUrl: "https://sampwiki.blast.hk/wroot/images2/4/48/Icon_41.gif",
    popup: (r) => (
      <>
        <div>
          <strong>Industrial site</strong>
        </div>
        <div>
          <strong>{r.name}</strong>
        </div>
        {r.producing !== "" ? (
          <div>
            Produces
            <ul>
              {r.producing
                .split(",")
                .filter((x) => x.length)
                .map((x) => (
                  <li key={x}>{x}</li>
                ))}
            </ul>
          </div>
        ) : null}
        {r.accepting !== "" ? (
          <div>
            Accepts
            <ul>
              {r.accepting
                .split(",")
                .filter((x) => x.length)
                .map((x) => (
                  <li key={x}>{x}</li>
                ))}
            </ul>
          </div>
        ) : null}
      </>
    )
  },
  {
    name: "GPS areas",
    apiUrl: "/api/areas",
    popup: (r) => (
      <>
        <div>
          <strong>GPS area</strong>
        </div>
        <div>
          <strong>{r.name}</strong>
        </div>
      </>
    ),
    featuresFn(rs) {
      var groups = {
        0: {} as Record<string, any[]>,
        1: {} as Record<string, any[]>
      };

      for (var r of rs) {
        (groups[r.main][r.name] = groups[r.main][r.name] || []).push(r);
      }

      for (var { main, entries } of [1, 0].map((x: 1 | 0) => ({
        main: !!x,
        entries: groups[x]
      }))) {
        for (var [name, group] of Object.entries(entries)) {
          var area = group
            .map(
              (r) =>
                bboxPolygon(
                  bbox(
                    points([
                      [r.y2, r.x2],
                      [r.y1, r.x1]
                    ])
                  )
                ) as Feature<Polygon | MultiPolygon>
            )
            .reduce((a, b) => union(a, b));

          this.features.addLayer(
            L.polygon(area.geometry.coordinates as any, {
              color: main ? "yellow" : "purple",
              fillColor: main ? "yellow" : "purple",
              //@ts-expect-error
              data: {
                group,
                name
              }
            }).bindTooltip(name, {
              permanent: main,
              interactive: false,
              direction: "center",
              className: main ? "area" : "subarea"
            })
          );
        }
      }
    }
  }
].map((_) => {
  var layer = Object.assign(_, {
    icon: _.iconUrl
      ? L.icon({
          iconUrl: ctUrl(_.iconUrl),
          iconSize: [16, 16]
        })
      : undefined,
    apiUrl: ctUrl(_.apiUrl),
    loaded: false,
    loading: false,
    features: L.featureGroup().bindPopup((f) => {
      var data = (f as any).options.data;
      var node = layer.popup(data);
      ReactDOM.render(node, popupContainer);
      return popupContainer;
    }),
    featureFn:
      _.featureFn ||
      ((r) =>
        L.marker([r[layer.y || "y"], r[layer.x || "x"]], {
          icon: layer.icon,
          //@ts-expect-error
          data: r
        })),
    featuresFn:
      _.featuresFn ||
      ((rs) =>
        rs.forEach((r) =>
          layer.features.addLayer(layer.featureFn.call(layer, r))
        ))
  });
  return layer;
});

async function apiGet(url, page) {
  return await (
    await fetch(
      `${url}${url.includes("?") ? "&" : "?"}page_size=1000&page=${page}`
    )
  ).json();
}

async function* apiGetAll(url) {
  var page = 1;
  var next = null;
  do {
    if (!next && page === 1) {
      next = apiGet(url, page);
    }
    var data = await next;
    if (data.next) {
      next = apiGet(url, ++page);
    } else {
      next = null;
    }
    yield data;
  } while (next);
}

async function loadLayerData(layer) {
  layer.loading = true;

  for await (let data of apiGetAll(layer.apiUrl)) {
    layer.featuresFn.call(layer, data.results);
  }

  layer.loaded = true;
  layer.loading = false;
}

async function setLayerVisibility(layer, visible) {
  if (visible) {
    layer.features.addTo(map);
    layer.visible = true;
    if (!layer.loaded && !layer.loading) {
      await loadLayerData(layer);
    }
  } else {
    layer.features.removeFrom(map);
    layer.visible = false;
  }
}

layers.forEach((layer) => {
  if (layer.visible) setLayerVisibility(layer, true);
});

var togglesContainer = document.createElement("div");
var renderToggles = () => {
  ReactDOM.render(
    <div className="layers">
      {layers.map((x) => (
        <label className="layer" key={x.apiUrl} style={{ display: "block" }}>
          <span>{x.name}</span>
          <input
            type="checkbox"
            defaultChecked={x.visible}
            onChange={(e) => setLayerVisibility(x, e.target.checked)}
          />
          {x.icon ? (
            <img src={x.icon.options.iconUrl} />
          ) : (
            <div
              style={{ display: "inline-block", width: "16px", height: "16px" }}
            />
          )}
        </label>
      ))}
    </div>,
    togglesContainer
  );
};
new (L.Control.extend({
  onAdd: function () {
    renderToggles();
    return togglesContainer;
  }
}))({
  position: "topright"
}).addTo(map);
